import React, { useEffect, useState, useCallback } from "react";
import {
    Container,
    Spinner,
    Input,
    Row,
    Col,
    Button
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import CommonDataTable from "../../common/DataTable";
import { getFarmhouseColumns } from "../../Components/Common/columns/Farmhouse";
import { fetchFarmhouses, removeFarmhouse, updateFarmhouseStatus } from "../../slices/farmhouse/thunk";
import { getLocations } from "../../helpers/apis";
import DeleteDialog from "../../Components/Common/DeleteDailog";
import FarmhouseCreate from "./FarmhouseCreate";
import FarmhouseDetailsModal from "./FarmhouseDetail";
import BookingHistoryModal from "./BookingHistoryModal";

const FarmhouseList = () => {
    const dispatch = useDispatch();
    const { farmhouses, pagination, loading } = useSelector((state) => ({
        farmhouses: state.Farmhouse.farmhouses || [],
        pagination: state.Farmhouse.pagination || {
            page: 1,
            limit: 10,
            totalPages: 0,
            totalFarmhouses: 0,
        },
        loading: state.Farmhouse.loading,
    }));

    const [filterParams, setFilterParams] = useState({
        page: 1,
        limit: 10,
        search: "",
        nearby: "",
        sort: "desc",
    });

    const [nearbyOptions, setNearbyOptions] = useState([]);

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [selectedFarmhouseId, setSelectedFarmhouseId] = useState(null);
    const [createEditModalOpen, setCreateEditModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editMode, setEditMode] = useState(null); // 'details' or 'amenities_images'
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [selectedHistoryFarmhouse, setSelectedHistoryFarmhouse] = useState(null);

    useEffect(() => {
        const fetchNearbyLocations = async () => {
            try {
                const response = await getLocations();
                if (!response.error && response.data) {
                    const locations = response.data.locations || [];
                    // Extract unique nearby values
                    const uniqueNearby = [...new Set(locations.map(loc => loc.nearby).filter(Boolean))];
                    setNearbyOptions(uniqueNearby.map(loc => ({ value: loc, label: loc })));
                }
            } catch (error) {
                console.error("Error fetching locations:", error);
            }
        };
        fetchNearbyLocations();
    }, []);

    useEffect(() => {
        const cleanedParams = {};
        Object.keys(filterParams).forEach(key => {
            const value = filterParams[key];
            if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
                cleanedParams[key] = value;
            }
        });
        dispatch(fetchFarmhouses(cleanedParams));
    }, [dispatch, filterParams]);

    const handleFetchData = useCallback((newParams) => {
        setFilterParams((prevParams) => {
            const updatedParams = {
                ...prevParams,
                ...newParams,
                limit: newParams.perPage || newParams.limit || prevParams.limit,
                page: newParams.currentPage || newParams.page || prevParams.page,
            };
            delete updatedParams.perPage;
            delete updatedParams.currentPage;
            return updatedParams;
        });
    }, []);

    const handleRowClick = (row) => {
        setSelectedFarmhouseId(row.id);
        setDetailsModalOpen(true);
    };

    const handleEditClick = (row, mode = 'choice') => {
        setEditingId(row.id);
        setEditMode(mode);
        if (mode === 'choice') {
            setCreateEditModalOpen(true);
        } else {
            setCreateEditModalOpen(true);
        }
    };

    const handleDeleteClick = (row) => {
        setDeleteId(row.id);
        setDeleteModalOpen(true);
    };

    const handleAddAmenitiesClick = (row) => {
        setEditingId(row.id);
        setEditMode('amenities_images');
        setCreateEditModalOpen(true);
    };

    const handleHistoryClick = (row) => {
        setSelectedHistoryFarmhouse({
            id: row.id,
            name: row.name
        });
        setHistoryModalOpen(true);
    };

    const handleStatusChange = async (farmhouseId, newStatus) => {
        try {
            await dispatch(updateFarmhouseStatus({
                id: farmhouseId,
                status: newStatus
            })).unwrap();

            // Refresh the farmhouses list
            const cleanedParams = {};
            Object.keys(filterParams).forEach(key => {
                const value = filterParams[key];
                if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
                    cleanedParams[key] = value;
                }
            });
            dispatch(fetchFarmhouses(cleanedParams));
        } catch (error) {
            console.error("Error updating farmhouse status:", error);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteId) return;
        setDeleting(true);
        try {
            await dispatch(removeFarmhouse(deleteId)).unwrap();
            setDeleteModalOpen(false);
            setDeleteId(null);
            const cleanedParams = {};
            Object.keys(filterParams).forEach(key => {
                const value = filterParams[key];
                if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
                    cleanedParams[key] = value;
                }
            });
            dispatch(fetchFarmhouses(cleanedParams));
        } catch (error) {
            console.error("Error deleting farmhouse:", error);
        } finally {
            setDeleting(false);
        }
    };

    const handleNearbyChange = (e) => {
        const nearby = e.target.value;
        setFilterParams(prev => ({
            ...prev,
            nearby: nearby,
            page: 1,
        }));
    };

    const handleSortChange = (e) => {
        const sort = e.target.value;
        setFilterParams(prev => ({
            ...prev,
            sort: sort,
            page: 1,
        }));
    };

    const handleClearFilters = () => {
        setFilterParams({
            page: 1,
            limit: 10,
            search: "",
            nearby: "",
            sort: "desc",
        });
    };

    const columns = getFarmhouseColumns({
        onDeleteClick: handleDeleteClick,
        onEditClick: handleEditClick,
        onRowClick: handleRowClick,
        onAddAmenitiesClick: handleAddAmenitiesClick,
        onStatusChange: handleStatusChange,
        onHistoryClick: handleHistoryClick,
    });

    const totalRows = pagination.totalFarmhouses || farmhouses.length;

    // Sort farmhouses by priority: HIGH > MEDIUM > LOW
    const priorityOrder = { 'HIGH': 3, 'MEDIUM': 2, 'LOW': 1 };
    const sortedFarmhouses = [...farmhouses].sort((a, b) => {
        const pA = priorityOrder[String(a.priority || '').toUpperCase()] || 0;
        const pB = priorityOrder[String(b.priority || '').toUpperCase()] || 0;
        return pB - pA;
    });

    const customFilters = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <div>
                <Input
                    type="select"
                    value={filterParams.nearby || ""}
                    onChange={handleNearbyChange}
                    style={{ minWidth: "150px" }}
                >
                    <option value="">All Locations</option>
                    {nearbyOptions.map((option, index) => (
                        <option key={index} value={option.value}>{option.label}</option>
                    ))}
                </Input>
            </div>
        </div>
    );

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Farmhouses" pageTitle="Farmhouses" />
                </Container>
            </div>

            <div className="table-container farmhouse-creator">
                {loading && farmhouses.length === 0 ? (
                    <div className="text-center p-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Loading farmhouses...</p>
                    </div>
                ) : (
                    <CommonDataTable
                        columns={columns}
                        data={sortedFarmhouses}
                        loading={loading}
                        totalRows={totalRows}
                        fetchData={handleFetchData}
                        filterParams={filterParams}
                        searchEnable={true}
                        checkboxEnabled={true}
                        customFilters={customFilters}
                        showExportButton={true}
                        exportButtonLabel="Export Farmhouses"
                        showAddButton={true}
                        buttonName="Add Farmhouse"
                        addOnClickMethod={() => {
                            setEditingId(null);
                            setEditMode(null);
                            setCreateEditModalOpen(true);
                        }}
                    />
                )}
            </div>

            <DeleteDialog
                isOpen={deleteModalOpen}
                title="Delete Farmhouse"
                message="Are you sure you want to delete this farmhouse? This action cannot be undone."
                confirmLabel="Delete"
                cancelLabel="Cancel"
                confirmColor="danger"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteModalOpen(false)}
                loading={deleting}
            />

            <FarmhouseDetailsModal
                isOpen={detailsModalOpen}
                toggle={() => setDetailsModalOpen(false)}
                farmhouseId={selectedFarmhouseId}
                onEditClick={(id, mode) => {
                    setDetailsModalOpen(false);
                    setTimeout(() => {
                        setEditingId(id);
                        setEditMode(mode);
                        setCreateEditModalOpen(true);
                    }, 300);
                }}
            />

            {/* Farmhouse Create/Edit Modal */}
            {createEditModalOpen && (
                <FarmhouseCreate
                    id={editingId}
                    isModal={true}
                    editMode={editMode}
                    onClose={() => {
                        setCreateEditModalOpen(false);
                        setEditMode(null);
                        setEditingId(null);
                    }}
                    onSuccess={() => {
                        setCreateEditModalOpen(false);
                        setEditMode(null);
                        setEditingId(null);
                        const cleanedParams = {};
                        Object.keys(filterParams).forEach((key) => {
                            const value = filterParams[key];
                            if (value !== "" && value !== undefined && value !== null && key !== "currentPage") {
                                cleanedParams[key] = value;
                            }
                        });
                        dispatch(fetchFarmhouses(cleanedParams));
                    }}
                />
            )}

            {/* Booking History Modal */}
            <BookingHistoryModal
                isOpen={historyModalOpen}
                toggle={() => {
                    setHistoryModalOpen(false);
                    setSelectedHistoryFarmhouse(null);
                }}
                farmhouseId={selectedHistoryFarmhouse?.id}
                farmhouseName={selectedHistoryFarmhouse?.name}
            />
        </React.Fragment>
    );
};

export default FarmhouseList;